import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, IconButton, Stack, Typography } from '@mui/material';
import cx from 'classnames';
import React, { useState } from 'react';

import { MedicalAccordionProps } from './MedicalMeasures.types';
import MedicalTile from './MedicalTile';
import i18n from 'FRONTEND/utils/i18n';

const ECG_ALL_MEASURES = ['HR', 'PR', 'QT', 'QTcF', 'QRS', 'P', 'T', 'sokolowLyon', 'cornell'];


function MedicalAccordion({ type, icon, measuresForType, className, consultationDate }: MedicalAccordionProps) {
  const measureCount = Object.keys(measuresForType).length;
  const [expanded, setExpanded] = useState(false);

  const titleMap = {
    blood_pressure: i18n._('Blood pressure'),
    oximetry: i18n._('Oximetry'),
    glucose: i18n._('Glucose'),
    temperature: i18n._('Temperature'),
    ecg: i18n._('ECG'),
    other_medical_measurement: i18n._('Other medical measurement'),
  };

  const classes = cx('md', className, {
    'md--bp': type === 'blood_pressure',
    'md--temp': type === 'temperature',
    'md--oxi': type === 'oximetry',
    'md--glucose': type === 'glucose',
    'md--ecg': type === 'ecg',
    'md--other': type === 'other_medical_measurement',
  });

  // Because this uses i18n, we need it inside the component
  const getMeasureData = (measure, type) => {
    const { systolic, diastolic, map, pulseRate, spo2, flowRate, value, unit } = measure;
    switch (type) {
      case 'blood_pressure':
        return [
          systolic && diastolic && { label: `${i18n._('SYS')}/${i18n._('DIA')}`, value: `${systolic.value}/${diastolic.value}`, unit: diastolic.unit },
          systolic && !diastolic && { label: i18n._('SYS'), value: `${systolic.value}`, unit: systolic.unit },
          diastolic && !systolic && { label: i18n._('DIA'), value: `${diastolic.value}`, unit: diastolic.unit },
          map && { label: i18n._('MAP'), value: map.value, unit: map.unit },
          pulseRate && { label: i18n._('Pulse'), value: pulseRate.value, unit: pulseRate.unit },
        ];
      case 'oximetry':
        return [
          spo2 && { label: i18n._('SpO2'), value: spo2.value, unit: spo2.unit },
          pulseRate && { label: i18n._('Pulse'), value: pulseRate.value, unit: pulseRate.unit },
          flowRate && { label: i18n._('Flow rate'), value: flowRate.value, unit: flowRate.unit },
        ];
      case 'glucose':
        return [
          { value, unit },
        ];
      case 'temperature':
        return [
          { value, unit },
        ];
      case 'ecg':
        return Object.keys(measure).filter((key) => ECG_ALL_MEASURES.includes(key)).map((key) => measure[key]);
      default:
        return [];
    }
  };

  const Detail = ({ label, val, style } : { label: string, val?: string, style?: React.CSSProperties }) => (
    <div className='md__details' style={style}>
      <span className='md__detailsLabel'>{label}</span>
      {val}
    </div>
  );

  const renderMeasureTile = (measure) => {
    const shouldDisplayFilterWarning = type === 'ecg' && (measure.ecgMetadata?.ecgBaseline !== '0.05Hz' || measure.ecgMetadata?.ecgLowPassFilter !== '248Hz');

    return (
      <>
        <MedicalTile
          type={type}
          deviceName={measure.deviceName}
          measureDate={measure.date}
          context={measure.context}
          consultationDate={consultationDate}
          site={measure.site}
          measureData={getMeasureData(measure, type)}
          notes={measure.notes}
          imageSrc={type === 'ecg' && measure.file}
          fileLink={type === 'other_medical_measurement' ? measure.file : undefined}
          fileName={type === 'other_medical_measurement' ? measure.fileName : undefined}
          category={measure.category}
        />
        {type === 'ecg' && (
          <div style={{ columnCount: 2, paddingLeft: 16, marginBottom: 16 }}>
            {measure.ecgMetadata && (
              <>
                {measure.ecgMetadata?.ecgSpikes && <Detail label={i18n._('Pacemaker detected. The automatic measurements displayed may be less accurate.')} style={{ columnSpan: 'all' }} />}
                {shouldDisplayFilterWarning && <Detail label={i18n._('Clinical interpretation of the ECG report may be affected by the filter settings.')} style={{ columnSpan: 'all' }} />}
                {measure.ecgMetadata?.ecgSaturation && <Detail label={`${i18n._('The following leads include a saturated signal: ')} ${measure.ecgMetadata?.ecgSpikes}`} style={{ columnSpan: 'all' }} />}
                <Detail label={i18n._('Scale')} val={`${measure.ecgMetadata?.ecgSpeed}mm/s ${measure.ecgMetadata?.ecgGain}mm/mV`} />
                {measure.ecgMetadata?.ecgBaseline && measure.ecgMetadata?.ecgLowPassFilter && <Detail label={i18n._('Bandwidth')} val={`${measure.ecgMetadata?.ecgBaseline} - ${measure.ecgMetadata?.ecgLowPassFilter}`} />}
                <Detail label={i18n._('Sampling rate')} val={`${measure.ecgMetadata?.ecgSamplingRate}Hz`} />
                <Detail label={i18n._('Serial Number')} val={measure.ecgMetadata?.ecgSerialNumber} />
              </>
            )}
          </div>
        )}
      </>
    );
  };

  const renderAccordion = () => (
    <>
      <Accordion
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <AccordionSummary sx={{
          padding: 0,
          minHeight: 0,
          '& .MuiAccordionSummary-content': { margin: 0, display: 'flex', flexDirection: 'column' },
        }}
        >
          {renderMeasureTile(measuresForType[0])}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {measuresForType.slice(1).map((measure, index) => (
            <React.Fragment key={index}>
              {renderMeasureTile(measure)}
            </React.Fragment>
          ))}
        </AccordionDetails>
      </Accordion>
      <IconButton
        onClick={() => setExpanded(!expanded)}
        sx={{ width: '100%', borderRadius: 0 }}
      >
        <ExpandMoreIcon
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          }}
        />
      </IconButton>
    </>
  );

  const renderSingleMeasure = () => renderMeasureTile(measuresForType[0]);

  return (
    <div className={classes}>
      <div className='md__header'>
        <img src={icon} />
        <Stack direction='row' spacing={2} justifyContent='space-between' alignItems='center' width='100%' pr={1}>
          {titleMap[type]}
          {measureCount > 1 && <Typography bgcolor='primary.main' borderRadius={15} px={1}>{measureCount} {i18n._('measures')}</Typography>}
        </Stack>
      </div>
      <div>
        {measuresForType.length > 1 ? renderAccordion() : renderSingleMeasure()}
      </div>
    </div>
  );
}

export default MedicalAccordion;
